import * as fs from 'fs';
import * as path from 'path';
import * as crypto from 'crypto';
import { parse } from '@babel/parser';
import _traverse from '@babel/traverse';
import * as t from '@babel/types';

// Handle CJS default export interop in ESM context
const traverse = typeof _traverse === 'function' ? _traverse : (_traverse as any).default;

export interface PageRouteInfo {
  path?: string;
  index?: boolean;
  [key: string]: unknown;
}

export interface ParseRoutesOptions {
  /** If true, prefix all routes with basePath. Default: true */
  applyBasePath?: boolean;
}

export function routeParserLog(
  level: 'log' | 'warn' | 'error' | 'info',
  message: string,
  ...args: unknown[]
): void {
  const prefix = '[route-parser]';
  const logMessage = `${prefix} ${message}`;
  switch (level) {
    case 'warn':
      console.warn(logMessage, ...args);
      break;
    case 'error':
      console.error(logMessage, ...args);
      break;
    case 'info':
      console.info(logMessage, ...args);
      break;
    default:
      console.log(logMessage, ...args);
  }
}

export function calculateFileHash(filePath: string): string | null {
  try {
    const content = fs.readFileSync(filePath, 'utf-8');
    return crypto.createHash('md5').update(content).digest('hex');
  } catch (error) {
    routeParserLog('warn', 'Failed to calculate file hash:', (error as Error).message);
    return null;
  }
}

export function isRouteComponent(openingElement: t.JSXOpeningElement): boolean {
  return (
    t.isJSXIdentifier(openingElement.name) &&
    openingElement.name.name === 'Route'
  );
}

export function evaluateTemplateLiteral(templateLiteral: t.TemplateLiteral): string {
  const quasis = templateLiteral.quasis;
  const expressions = templateLiteral.expressions;

  if (quasis.length === 1 && expressions.length === 0) {
    return quasis[0].value.raw;
  }

  return quasis.map((q) => q.value.raw).join('');
}

export function extractPageRouteInfo(openingElement: t.JSXOpeningElement): PageRouteInfo {
  const routeInfo: PageRouteInfo = {};

  openingElement.attributes.forEach((attr) => {
    if (t.isJSXAttribute(attr) && t.isJSXIdentifier(attr.name)) {
      const name = attr.name.name;
      let value: unknown;

      if (attr.value) {
        if (t.isStringLiteral(attr.value)) {
          value = attr.value.value;
        } else if (t.isJSXExpressionContainer(attr.value)) {
          const expression = attr.value.expression;
          if (t.isStringLiteral(expression)) {
            value = expression.value;
          } else if (t.isTemplateLiteral(expression)) {
            value = evaluateTemplateLiteral(expression);
          } else {
            value = true;
          }
        }
      } else {
        value = true;
      }

      routeInfo[name] = value;
    }
  });

  return routeInfo;
}

export function buildFullPath(
  routeStack: PageRouteInfo[],
  currentRoute: PageRouteInfo
): string | null {
  let fullPath = '';

  for (let i = 0; i < routeStack.length; i++) {
    if (routeStack[i].path) {
      let parentPath = routeStack[i].path!;
      if (!parentPath.startsWith('/')) parentPath = `/${parentPath}`;
      if (parentPath.endsWith('/') && parentPath !== '/') {
        parentPath = parentPath.slice(0, -1);
      }

      fullPath += parentPath === '/' ? '' : parentPath;
    }
  }

  if (currentRoute.index) {
    return fullPath || '/';
  } else if (currentRoute.path) {
    const routePath = currentRoute.path;

    if (routePath === '*') {
      return null;
    }

    if (!routePath.startsWith('/')) {
      fullPath = `${fullPath}/${routePath}`;
    } else {
      fullPath = routePath;
    }

    if (fullPath === '') fullPath = '/';
    if (!fullPath.startsWith('/')) fullPath = `/${fullPath}`;

    return fullPath;
  }

  return null;
}

/**
 * Parse routes from the app file at build time.
 * @param appPath - Path to app.tsx (relative to cwd or absolute)
 * @param basePath - Normalized base path prefix (e.g., '/my_plugin', or '' for no prefix)
 * @param options - Parse options
 * @returns Array of route definitions
 */
export function parseRoutesFromFile(
  appPath: string,
  basePath: string,
  options: ParseRoutesOptions = {}
): Array<{ path: string }> {
  const { applyBasePath = true } = options;
  const defaultPath = applyBasePath && basePath ? `${basePath}/` : '/';

  try {
    const appFilePath = path.resolve(process.cwd(), appPath);

    if (!fs.existsSync(appFilePath)) {
      throw new Error(`App file does not exist: ${appFilePath}`);
    }

    const sourceCode = fs.readFileSync(appFilePath, 'utf-8');

    const ast = parse(sourceCode, {
      sourceType: 'module',
      plugins: [
        'jsx',
        'typescript',
        'decorators-legacy',
        'classProperties',
        'objectRestSpread',
        'functionBind',
        'exportDefaultFrom',
        'exportNamespaceFrom',
        'dynamicImport',
        'nullishCoalescingOperator',
        'optionalChaining',
      ],
    });

    const routeSet = new Set<string>();
    const routeStack: PageRouteInfo[] = [];

    traverse(ast, {
      JSXElement: {
        enter(nodePath: any) {
          const { openingElement } = nodePath.node;
          if (isRouteComponent(openingElement)) {
            routeStack.push(extractPageRouteInfo(openingElement));
          }
        },
        exit(nodePath: any) {
          const { openingElement } = nodePath.node;
          if (isRouteComponent(openingElement)) {
            const currentRoute = routeStack.pop();
            if (currentRoute && currentRoute.path === '*') return;
            if (currentRoute && (currentRoute.path || currentRoute.index)) {
              const fullPath = buildFullPath(routeStack, currentRoute);
              if (fullPath) routeSet.add(fullPath);
            }
          }
        },
      },
    });

    const routes = Array.from(routeSet).map((routePath) => ({
      path: applyBasePath && basePath ? `${basePath}${routePath}` : routePath,
    }));
    return routes.length > 0 ? routes : [{ path: defaultPath }];
  } catch (error) {
    routeParserLog('warn', 'Route parsing failed, using default routes:', (error as Error).message);
    return [{ path: defaultPath }];
  }
}
